import React, { useRef } from "react";
import PropTypes from "prop-types";
import { SmartList } from "../../../ng-components/List";
import { IconButton, LinkButton } from "../../../ng-components/Button";
import PrevIcon from "../../../images/arrow-down.svg";
import NextIcon from "../../../images/arrow-down.svg";
import { useTheme } from "../../../ng-components/theme";

const pageGutter = 1.25;
const gutterWidth = 2;

// This component assumes it stretches from one end of the viewport to the other
export function CarouselCommon({
    className = "",
    heading,
    cards,
    onViewAll = null,
    maxWidth = 19,
    isSeries = false,
}) {
    const carouselRef = useRef(null);
    const { contrastTextColor, contentWidth, modularScale } = useTheme();
    const getListAndItems = () => {
        return [
            carouselRef.current.getElementsByClassName(
                "card-carousel__list"
            )[0],
            [
                ...carouselRef.current.getElementsByClassName(
                    "card-carousel__list-item"
                ),
            ],
        ];
    };
    const onPrevClick = () => {
        const [list, items] = getListAndItems();
        const {
            left: listLeftEnd,
            right: listRightEnd,
        } = list.getBoundingClientRect();
        const firstOffScreenItem = items.find((item) => {
            const { right } = item.getBoundingClientRect();
            return right >= listLeftEnd + gutterWidth * 16;
        });
        if (firstOffScreenItem) {
            list.scrollLeft -=
                listRightEnd -
                pageGutter * 16 -
                firstOffScreenItem.getBoundingClientRect().right;
        }
    };
    const onNextClick = () => {
        const [list, items] = getListAndItems();
        const {
            left: listLeftEnd,
            right: listRightEnd,
        } = list.getBoundingClientRect();
        const firstOffScreenItem = items.find((item) => {
            const { right } = item.getBoundingClientRect();
            return right >= listRightEnd;
        });
        if (firstOffScreenItem) {
            list.scrollLeft +=
                firstOffScreenItem.getBoundingClientRect().left -
                (listLeftEnd + (gutterWidth + pageGutter) * 16);
        }
    };

    return (
        <div ref={carouselRef} className={`card-carousel ${className}`}>
            <header className="card-carousel__header">
                <h2 className="card-carousel__heading override-global-styling">
                    {heading}
                </h2>
                {cards.length > 1 && (
                    <div className="card-carousel__actions">
                        <IconButton
                            className="card-carousel__prev-button"
                            aria-label="Previous"
                            icon={PrevIcon}
                            onClick={onPrevClick}
                        />
                        <IconButton
                            className="card-carousel__next-button"
                            aria-label="Next"
                            icon={NextIcon}
                            onClick={onNextClick}
                        />
                    </div>
                )}
            </header>
            <SmartList
                solitaryItemClassName="card-carousel__solitary-list-item"
                listClassName="card-carousel__list"
                itemClassName="card-carousel__list-item"
                items={cards}
            />
            <style jsx>{`
                .card-carousel__header {
                    padding: 2em ${pageGutter}em 1.5em;
                    margin: 0 auto;
                    max-width: ${contentWidth + pageGutter * 2}em;

                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                }
                .card-carousel__heading.override-global-styling {
                    display: inline-block;
                    vertical-align: middle;
                    margin: 0 0.5em 0 0;
                    color: ${contrastTextColor};
                    font-size: ${modularScale(4)}em;
                }
                .card-carousel__actions {
                    flex: 1 1 auto;
                    display: flex;
                }
                .card-carousel {
                    position: relative;
                }
                .card-carousel :global(.card-carousel__prev-button),
                .card-carousel :global(.card-carousel__next-button) {
                    height: 4rem;
                    width: 4rem;
                    top: 48%;
                    position: absolute;
                    z-index: 1;
                    box-shadow: 0px 0px 8px 2px rgb(0 0 0 / 20%);                    
                }
                .card-carousel :global(.card-carousel__next-button) {
                    transform: rotate(-90deg);
                    right: 4rem;
                }
                .card-carousel :global(.card-carousel__prev-button) {
                    transform: rotate(90deg);
                }
                .card-carousel :global(.card-carousel__see-all) {
                    margin: 0.25em 0 0.25em auto;
                    color: ${contrastTextColor};
                    line-height: 1;
                    font-weight: 600;
                    font-size: 1em;
                }

                .card-carousel :global(.card-carousel__list) {
                    margin: -${gutterWidth}em 0 0 -${gutterWidth}em;
                    padding: 0;
                    list-style-type: none;
                    white-space: nowrap;
                    overflow: auto;
                    scroll-behavior: smooth;

                    // Ensure any box-shadow does not get clipped
                    padding-bottom: 0.75em;

                    display: flex;

                    // Hide scrollbar but allow scrolling
                    -ms-overflow-style: none; // Internet Explorer 10+
                    scrollbar-width: none; // Firefox
                }
                .card-carousel
                    :global(.card-carousel__list::-webkit-scrollbar) {
                    display: none; // Safari and Chrome
                }

                .card-carousel :global(.card-carousel__list:before) {
                    content: "";
                    flex: none;
                    width: ${pageGutter}em;
                }
                .card-carousel :global(.card-carousel__list:after) {
                    content: "";
                    flex: none;
                    padding-left: ${pageGutter}em;
                }
                @media (min-width: ${contentWidth + gutterWidth * 2}em) {
                    .card-carousel :global(.card-carousel__list:before) {
                        width: calc(
                            (100% - ${contentWidth + gutterWidth}em) / 2
                        );
                    }
                    .card-carousel :global(.card-carousel__list:after) {
                        padding-left: calc(
                            (100% - ${contentWidth + gutterWidth}em) / 2
                        );
                    }
                }

                .card-carousel :global(.card-carousel__solitary-list-item) {
                    margin: 0 ${pageGutter}em;
                    max-width: 23em;
                }
                .card-carousel
                    :global(.card-carousel__solitary-list-item:after) {
                    content: "";
                    flex: 0 0 auto;
                    width: 0;
                }
                .card-carousel :global(.card-carousel__list-item) {
                    margin: ${gutterWidth + 1}em 0 0 ${gutterWidth}em;
                    box-sizing: border-box;
                    display: inline-block;
                    vertical-align: top;
                    white-space: normal;

                    width: 70%;
                    max-width: 23em;

                    flex: 1 0 auto;
                }
                @media (min-width:400px) and (max-width: 767px) {
                    .card-carousel :global(.card-carousel__list-item) {
                        ${ isSeries? 
                            `margin: ${gutterWidth + 1}em 0 0 2em;
                             max-width: 19em;
                             `:''}                        
                    }
                }
                @media (max-width: 400px) {
                    .card-carousel :global(.card-carousel__list-item) {
                        margin: ${ !isSeries? `${gutterWidth + 1}em 0 0 ${gutterWidth+4}em;`:
                            `${gutterWidth + 1}em 0 0 3em;`}
                    }
                    .card-carousel :global(.card-carousel__list-item):first-child  {
                        ${!isSeries?'margin-left: 4rem !important':''}
                    }
                }
                @media (max-width: 320px) {
                    .card-carousel :global(.card-carousel__list-item) {
                        margin: ${ !isSeries? `${gutterWidth + 1}em 0 0 ${gutterWidth+6}em;`:
                            `${gutterWidth + 1}em 0 0 4em;`}
                    }
                }

                @media(max-width: 768px){
                    .card-carousel :global(.card-carousel__prev-button),
                    .card-carousel :global(.card-carousel__next-button) {
                        display: none;
                    }
                    .card-carousel :global(.card-carousel__list-item):first-child  {
                        ${!isSeries?'margin-left: -5rem':''}
                    }
                    .card-carousel :global(.card-carousel__list-item):last-child  {
                        ${!isSeries?'margin-right: 10rem':''}
                    }
                }

                @media (min-width: 48em) {
                    .card-carousel
                        :global(.card-carousel__solitary-list-item:after) {
                        width: calc(100% - ${maxWidth}em);
                    }
                    .card-carousel :global(.card-carousel__solitary-list-item),
                    .card-carousel :global(.card-carousel__list-item) {
                        max-width: ${maxWidth}em;
                    }
                }
                @media (min-width: 67em) {
                    .card-carousel
                        :global(.card-carousel__solitary-list-item:after) {
                        width: calc(100% - ${maxWidth}em);
                    }
                    .card-carousel :global(.card-carousel__solitary-list-item),
                    .card-carousel :global(.card-carousel__list-item) {
                        max-width: ${maxWidth}em;
                    }
                }
                @media (min-width: 72em) {
                    .card-carousel
                        :global(.card-carousel__solitary-list-item:after) {
                        width: calc(100% - ${maxWidth}em);
                    }
                    .card-carousel :global(.card-carousel__solitary-list-item),
                    .card-carousel :global(.card-carousel__list-item) {
                        max-width: ${maxWidth}em;
                    }
                }
                @media (min-width: 102em) {
                    .card-carousel
                        :global(.card-carousel__solitary-list-item:after) {
                        width: calc(100% - ${maxWidth}em);
                    }
                    .card-carousel :global(.card-carousel__solitary-list-item),
                    .card-carousel :global(.card-carousel__list-item) {
                        max-width: ${maxWidth}em;
                    }
                }
                @media (min-width: ${contentWidth}em) {
                    .card-carousel :global(.card-carousel__solitary-list-item) {
                        max-width: ${contentWidth}em;
                        margin: 0 auto;
                        display: flex;
                    }
                }
            `}</style>
        </div>
    );
}

CarouselCommon.propTypes = {
    className: PropTypes.string,
    heading: PropTypes.node.isRequired,
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            display: PropTypes.node,
        })
    ).isRequired,
    onViewAll: PropTypes.func,
};

CarouselCommon.defaultProps = {
    className: "",
    onViewAll: null,
};
